<template>
    <div :class="$style.container">
        <div :class="[$style.front, isBackVisible && $style.hideUnder]">
            <slot name="front" />
        </div>
        <div
            :class="[$style.back, isBackVisible && $style.visible]"
            @transitionend="handleTransitionBackEnd"
        >
            <AdContainer
                v-if="adMounted"
                :class="$style.mobile"
                :placement="adMobilePlacement"
                @update:visible="onAdVisibilityChanged"
            />
            <AdContainer
                v-if="adMounted"
                :class="$style.tablet"
                :placement="adTabletPlacement"
                @update:visible="onAdVisibilityChanged"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import AdContainer from '@/components/AdContainer/AdContainer.vue'

const props = withDefaults(
    defineProps<{
        frontDurationMs?: number
        backDurationMs?: number
        widgetMode?: boolean
    }>(),
    {
        frontDurationMs: 15000,
        backDurationMs: 60000,
    },
)

const isBackVisible = ref(false)
const adMounted = ref(false)
const REFETCH_FAILED_AD_MS = 60_000

const timerFront = ref<ReturnType<typeof setTimeout> | null>(null)
const timerBack = ref<ReturnType<typeof setTimeout> | null>(null)

const adMobilePlacement = computed(() => (props.widgetMode ? 'widget_sticky_mobile' : 'sticky_mobile'))
const adTabletPlacement = computed(() => (props.widgetMode ? 'widget_sticky_tablet' : 'sticky_tablet'))

function scheduleNextAd(customDurationMs?: number) {
    resetTimers()
    timerFront.value = setTimeout(() => {
        adMounted.value = true
    }, customDurationMs ?? props.frontDurationMs)
}

function scheduleFailAd() {
    adMounted.value = false
    isBackVisible.value = false
    scheduleNextAd(REFETCH_FAILED_AD_MS)
}

function scheduleNextFront() {
    timerBack.value = setTimeout(() => {
        isBackVisible.value = false
    }, props.backDurationMs)
}

function resetTimers() {
    clearTimeout(timerFront.value!)
    clearTimeout(timerBack.value!)
}

function onAdVisibilityChanged(isVisible: boolean) {
    if (isVisible) {
        isBackVisible.value = true
        scheduleNextFront()
    } else {
        scheduleFailAd()
    }
}

function handleTransitionBackEnd() {
    if (!isBackVisible.value) {
        adMounted.value = false
        scheduleNextAd()
    }
}

onMounted(() => {
    scheduleNextAd()
})

onBeforeUnmount(() => {
    resetTimers()
})
</script>

<style module>
.container {
    background-color: var(--black);
    position: relative;
    height: 100%;
    overflow: hidden;
}

.front,
.back {
    position: absolute;
    inset: 0;
    transition: transform 0.6s ease;
}

.hideUnder {
    transform: translateY(-100%);
}

.back {
    transform: translateY(100%);
}

.back.visible {
    transform: translateY(0);
}

@media (prefers-color-scheme: light) {
    .container {
        background-color: var(--white);
    }
}

.tablet {
    display: none;
}

@media (--tablet-plus) {
    .tablet {
        display: block;
    }

    .mobile {
        display: none;
    }
}
</style>
